import axios, { AxiosHeaders, CanceledError, HttpStatusCode } from "axios";
import PaginatedResponse from "common/contracts/paginated-response";
import AttorneyHubAPIService from "common/services/api/attorney-hub-api-service";
import Guid from "common/values/guid/guid";
import { enqueueSnackbar } from "notistack";
import User from "users/entities/user/user";
import Session from "users/session/session";
import {
  SaveProposalAPIRequest,
  ShareProposalAPIRequest
} from "work/entities/proposal/api/request-contracts/update-proposal-api-request";
import WorkProposalAPIRequest from "work/entities/proposal/api/request-contracts/work-proposal-api-request";
import DetailedWorkProposalAPIResponse from "work/entities/proposal/api/response-contracts/detailed-work-proposal-api-response";
import SimpleWorkProposalAPIResponse from "work/entities/proposal/api/response-contracts/simple-work-proposal-api-response";
import WorkProposalAPIResponse from "work/entities/proposal/api/response-contracts/work-proposal-api-response";
import Proposal, { IProposalStore } from "work/entities/proposal/proposal";
import ProposalRedline from "work/entities/proposal/redlining/proposal-redline";
import ProposalBuilder from "work/entities/proposal/utils/proposal-builder";
import { IWorkAgreementStore } from "work/entities/work-agreement/work-agreement";
import { ProposalStatus } from "work/values/constants";
import SimpleWorkVendorTeamAPIResponse from "work/values/vendor-team/api/response-contracts/simple-work-vendor-team-api-response";
import { ProposalQuery } from "../store/proposals-redux-slice";
import BarebonesProposalInfoAPIResponse from "./response-contracts/barebones-proposal-info-api-response";

export default class ProposalAPIService implements IProposalStore {

  private readonly authHeaders: AxiosHeaders = new AxiosHeaders();
  private readonly currentUser: User;
  private readonly workAgreementStore: IWorkAgreementStore;

  private authHeadersWithJson(): AxiosHeaders {
    return this.authHeaders.concat({'Content-Type': 'application/json'});
  }

  constructor(session: Readonly<Session>, workAgreementStore: IWorkAgreementStore) {
    if (!session.authToken?.value)
      throw new Error("Session must have an authToken to create a ProposalAPIService");
    this.authHeaders.set(
      "Authorization",
      `Bearer ${session.authToken.value}`
    );
    if (!session.user) {
      throw new Error("Session must have a user to create a ProposalAPIService");
    }
    this.currentUser = session.user;
    this.workAgreementStore = workAgreementStore;
  }

  async getProposals(query: ProposalQuery, abortController?: AbortController): Promise<PaginatedResponse<Proposal>> {
    try {
      const queryString = query.asSearchParams().toString()
      const url = new URL(
        `/work/proposals?${queryString}`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.get(
        url.toString(),
        {
          headers: this.authHeaders,
          signal: abortController?.signal
        }
      );
      const responseData: SimpleWorkProposalAPIResponse[] = response.data;
      const proposals = responseData.map((proposalData) => {
          const team = Object.assign(
            new SimpleWorkVendorTeamAPIResponse(),
            proposalData.vendorTeam
          ).deserialize();
          return Object.assign(
            new SimpleWorkProposalAPIResponse(),
            proposalData
          ).deserialize(
            this,
            this.workAgreementStore,
            this.currentUser,
            team
          )
        }
      );

      return PaginatedResponse.from<Proposal>(
        proposals,
        response.headers['x-pagination']
      );
    } catch (error) {
      console.error(error);
      if (error instanceof CanceledError)
        throw error;
      throw new ProposalAPIServiceError(
        "getProposals",
        error
      );
    }
  }

  async getProposalById(id: Guid, abortController?: AbortController): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }> {
    try {
      const url = new URL(
        `/work/proposals/${id.value}`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.get(
        url.toString(),
        {
          headers: this.authHeaders,
          signal: abortController?.signal
        }
      );
      const responseData: DetailedWorkProposalAPIResponse = Object.assign(
        new DetailedWorkProposalAPIResponse(),
        response.data
      );
      return responseData.deserializeDetailed(
        this,
        this.workAgreementStore,
        this.currentUser
      );
    } catch (error) {
      console.error(error);
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.Forbidden) {
        throw new ProposalForbiddenError(id);
      }
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound) {
        throw new ProposalNotFoundError(id);
      }
      if (error instanceof CanceledError)
        throw error;
      throw new ProposalAPIServiceError(
        "getProposalById",
        error
      );
    }
  }

  async getProposalsByIds(
    ids: Guid[],
    abortController?: AbortController
  ): Promise<BarebonesProposalInfoAPIResponse[]> {
    let url = new URL(
      "/work/proposals/get-by-ids",
      AttorneyHubAPIService.apiBaseUrl
    );
    try {
      const response = await axios.post(
        url.toString(),
        ids.map((id) => id.value),
        {
          headers: this.authHeaders,
          signal: abortController?.signal
        }
      );
      return response.data.map((proposalData: Partial<BarebonesProposalInfoAPIResponse>) =>
        Object.assign(
          new BarebonesProposalInfoAPIResponse(),
          proposalData
        )
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest)
        throw new ProposalNotFoundError(error.response?.data);
      if (error instanceof CanceledError)
        throw error;
      throw new ProposalAPIServiceError(
        "getProposalsByIds",
        error
      );
    }
  }

  async createProposal(proposal: Proposal, session: Readonly<Session>): Promise<Proposal> {
    try {
      const request = new WorkProposalAPIRequest(
        proposal,
        session
      );
      const url = new URL(
        "/work/proposals",
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.post(
        url.toString(),
        request.payload,
        {
          headers: this.authHeadersWithJson()
        }
      );

      const responseData: DetailedWorkProposalAPIResponse = Object.assign(
        new DetailedWorkProposalAPIResponse(),
        response.data
      );
      const {proposal: responseProposal} = responseData.deserializeDetailed(
        this,
        this.workAgreementStore,
        this.currentUser
      );
      return responseProposal;
    } catch (error) {
      console.error(error);
      return Promise.reject(
        new Error(
          "Unable to create proposal",
          {cause: error}
        )
      );
    }
  }

  async saveProposal(updatedProposal: Proposal, proposalBuilderJSON: object): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }> {
    if (!updatedProposal.id)
      throw new Error("Cannot update proposal without id.");

    const request = new SaveProposalAPIRequest(
      updatedProposal,
      proposalBuilderJSON
    );

    if (request.payload.length === 0) {
      return {proposal: updatedProposal};
    }

    try {
      const url = new URL(
        `/work/proposals/${updatedProposal.id.value}`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.patch(
        url.toString(),
        request.payload,
        {
          headers: this.authHeadersWithJson()
        }
      );
      const responseData: DetailedWorkProposalAPIResponse = Object.assign(
        new DetailedWorkProposalAPIResponse(),
        response.data
      );
      return responseData.deserializeDetailed(
        this,
        this.workAgreementStore,
        this.currentUser
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound) {
        throw new ProposalNotFoundError(updatedProposal.id);
      }
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest) {
        throw new ProposalUpdateError(
          updatedProposal.id,
          error.response?.data
        );
      }
      throw new ProposalAPIServiceError(
        "updateProposal",
        error
      );
    }
  }

  async shareProposal(originalProposal: Proposal, updatedProposal: Proposal): Promise<Proposal> {
    if (!originalProposal.id)
      throw new Error("Cannot update proposal without id.");

    const request = new ShareProposalAPIRequest(
      originalProposal,
      updatedProposal
    );

    if (request.payload.length === 0) {
      return originalProposal;
    }

    try {
      const url = new URL(
        `/work/proposals/${originalProposal.id.value}`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.patch(
        url.toString(),
        request.payload,
        {
          headers: this.authHeadersWithJson()
        }
      );
      const responseData: DetailedWorkProposalAPIResponse = Object.assign(
        new DetailedWorkProposalAPIResponse(),
        response.data
      );
      const {proposal} = responseData.deserializeDetailed(
        this,
        this.workAgreementStore,
        this.currentUser
      );
      return proposal;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound) {
        throw new ProposalNotFoundError(originalProposal.id);
      }
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest) {
        throw new ProposalUpdateError(
          originalProposal.id,
          error.response?.data
        );
      }
      throw new ProposalAPIServiceError(
        "updateProposal",
        error
      );
    }
  }

  async deleteProposal(proposal: Proposal): Promise<void> {
    if (!proposal.id) {
      throw new Error("Cannot delete proposal without id.");
    }

    if (proposal.status !== ProposalStatus.AwaitingSubmission) {
      throw new Error("Cannot delete proposal that is not in draft status.");
    }

    try {
      const url = new URL(
        `/work/proposals/${proposal.id.value}`,
        AttorneyHubAPIService.apiBaseUrl
      );
      await axios.delete(
        url.toString(),
        {
          headers: this.authHeaders
        }
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound) {
        throw new ProposalNotFoundError(proposal.id);
      }
      throw new ProposalAPIServiceError(
        "deleteProposal",
        error
      );
    }
  }

  async removeDraftProposals(proposalIds: Guid[]): Promise<void> {
    if (proposalIds.length === 0) {
      return;
    }

    try {
      const url = new URL(
        `/work/draft-proposals`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.delete(
        url.toString(),
        {
          headers: this.authHeaders,
          data: {proposalIds: proposalIds.map(id => id.value)}
        }
      );
      if (response?.status === 207) {
        enqueueSnackbar(
          "Some drafts could not be removed",
          {variant: "warning"}
        );
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound) {
        throw new ProposalNotFoundError();
      }
      throw new ProposalAPIServiceError(
        "removeDraftProposals",
        error
      );
    }
  }

  async cancelProposal(proposal: Proposal): Promise<Proposal> {
    if (!proposal.id) {
      throw new Error("Cannot cancel proposal without id.");
    }

    try {
      const url = new URL(
        `/work/proposals/${proposal.id.value}/cancel`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.post(
        url.toString(),
        {},
        {
          headers: this.authHeaders
        }
      );

      return Object.assign(
        new WorkProposalAPIResponse(),
        response.data
      ).deserialize(
        this,
        this.workAgreementStore,
        this.currentUser
      );
    } catch (error) {
      throw new ProposalAPIServiceError(
        "cancelProposal",
        error
      );
    }
  }

  async hireProposal(proposal: Proposal): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }> {
    if (!proposal.id) {
      throw new Error("Cannot hire proposal without id.");
    }

    try {
      const url = new URL(
        `/work/proposals/${proposal.id.value}/hire`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.post(
        url.toString(),
        {},
        {
          headers: this.authHeaders
        }
      );

      return Object.assign(
        new DetailedWorkProposalAPIResponse(),
        response.data
      ).deserializeDetailed(
        this,
        this.workAgreementStore,
        this.currentUser
      );
    } catch (error) {
      throw new ProposalAPIServiceError(
        "hireProposal",
        error
      );
    }
  }

  async submitProposal(proposal: Proposal): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }> {
    if (!proposal.id) {
      throw new Error("Cannot submit proposal without id.");
    }

    try {
      const url = new URL(
        `/work/proposals/${proposal.id.value}/submit`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.post(
        url.toString(),
        {},
        {
          headers: this.authHeaders
        }
      );
      return Object.assign(
        new DetailedWorkProposalAPIResponse(),
        response.data
      ).deserializeDetailed(
        this,
        this.workAgreementStore,
        this.currentUser
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound) {
        throw new ProposalNotFoundError(proposal.id);
      }
      throw new ProposalAPIServiceError(
        "submitProposal",
        error
      );
    }
  }

  async approveProposal(proposal: Proposal, _session: Readonly<Session>): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }> {
    if (!proposal.id) {
      throw new Error("Cannot approve proposal without id.");
    }

    try {
      const url = new URL(
        `/work/proposals/${proposal.id.value}/approve`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.post(
        url.toString(),
        {},
        {
          headers: this.authHeaders
        }
      );
      return Object.assign(
        new DetailedWorkProposalAPIResponse(),
        response.data
      ).deserializeDetailed(
        this,
        this.workAgreementStore,
        this.currentUser
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound) {
        throw new ProposalNotFoundError(proposal.id);
      }
      throw new ProposalAPIServiceError(
        "approveProposal",
        error
      );
    }
  }

  async rejectProposal(proposal: Proposal): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }> {
    if (!proposal.id) {
      throw new Error("Cannot reject proposal without id.");
    }

    try {
      const url = new URL(
        `/work/proposals/${proposal.id.value}/reject`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.post(
        url.toString(),
        {},
        {
          headers: this.authHeaders
        }
      );
      return Object.assign(
        new DetailedWorkProposalAPIResponse(),
        response.data
      ).deserializeDetailed(
        this,
        this.workAgreementStore,
        this.currentUser
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound) {
        throw new ProposalNotFoundError(proposal.id);
      }
      throw new ProposalAPIServiceError(
        "rejectProposal",
        error
      );
    }
  }

  async requestRevision(proposal: Proposal, session: Readonly<Session>): Promise<{
    proposal: Proposal,
    userProposalBuilder?: ProposalBuilder,
    userRedlining?: ProposalRedline
  }> {
    if (!proposal.id) {
      throw new Error("Cannot request revision for proposal without id.");
    }

    try {
      const request = new WorkProposalAPIRequest(
        proposal.redlinedRevision,
        session
      ).payload;

      const url = new URL(
        `/work/proposals/${proposal.id.value}/request-revision`,
        AttorneyHubAPIService.apiBaseUrl
      );

      const response = await axios.post(
        url.toString(),
        request,
        {
          headers: this.authHeadersWithJson()
        }
      );
      return Object.assign(
        new DetailedWorkProposalAPIResponse(),
        response.data
      ).deserializeDetailed(
        this,
        this.workAgreementStore,
        this.currentUser
      );
    } catch (error) {
      throw new ProposalAPIServiceError(
        "requestRevision",
        error
      );
    }
  }

  async giveReviewerApproval(proposal: Proposal): Promise<void> {
    if (!proposal.id) {
      throw new Error("Cannot approve a proposal without id.");
    }
    try {
      const url = new URL(
        `/work/proposals/${proposal.id.value}/reviewer-approve`,
        AttorneyHubAPIService.apiBaseUrl
      );
      await axios.post(
        url.toString(),
        {},
        {
          headers: this.authHeaders
        }
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound) {
        throw new ProposalNotFoundError(proposal.id);
      }
      throw new ProposalAPIServiceError(
        "giveReviewerApproval",
        error
      );
    }
  }

  async quitProposalTeam(proposalId: Guid): Promise<void> {
    try {
      const url = new URL(
        `/work/proposals/${proposalId.value}/quit-team`,
        AttorneyHubAPIService.apiBaseUrl
      );
      await axios.post(
        url.toString(),
        {},
        {
          headers: this.authHeaders
        }
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound) {
        throw new ProposalNotFoundError(proposalId);
      } else if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest) {
        throw new ProposalTeamMemberQuitError(proposalId);
      }
      throw new ProposalAPIServiceError(
        "quitProposalTeam",
        error
      );
    }
  }
}

export class ProposalAPIServiceError extends Error {
  constructor(method: string, error: unknown) {
    super(`Error calling ProposalAPIService.${method}: Error: ${error}`);
  }
}

export class InvalidProposalContextError extends Error {
  constructor(context: string) {
    super(`Invalid viewing context ${context} for user.`);
  }
}

export class ProposalNotFoundError extends Error {
  constructor(proposalId?: Guid) {
    if (!proposalId)
      super("One or more proposals not found.");
    else
      super(`Proposal with id ${proposalId} not found.`);
  }
}

export class ProposalTeamMemberQuitError extends Error {
  constructor(proposalId: Guid) {
    super(`Error quitting proposal team for proposal ${proposalId}`);
  }
}

export class ProposalForbiddenError extends Error {
  constructor(proposalId: Guid) {
    super(`You do not have permission to view this proposal: ${proposalId}`);
  }
}

export class ProposalUpdateError extends Error {
  proposalId: Guid;

  constructor(proposalId: Guid, errorData?: object | string) {
    if (errorData) {
      super("Unable to update proposal: " + JSON.stringify(errorData));
      if (typeof errorData === "string") {
        this.message = errorData;
      } else if (typeof errorData === "object") {
        this.message = Object.keys(errorData).map((key) => `${key}: ${errorData[key as keyof typeof errorData]}`).join(", ");
      } else {
        this.message = "Unknown error."
      }
    } else {
      super("Unable to update proposal.");
    }
    this.name = "ProposalUpdateError";
    this.proposalId = proposalId;
  }
}
